import { ImageT } from '@lambda/apis/src/catalog-v2/types';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import DesktopNavigation from './navigation/Desktop';
import MobileNavigation from './navigation/Mobile';
import Placeholder from './Placeholder';
import { useSwiperSlider } from '@/components/commons/hooks';
import { generateCarouselImages, buildProductImageAlt } from './helpers';
import { useProductPageContext } from '@/components/v2/product/hooks/useProductPageContext';
import { getEdgeUrl } from '@/lib/getEdgeUrl';

import 'swiper/css';

type CarouselProps = {
  images: ImageT[];
};

const Carousel = ({ images }: CarouselProps) => {
  const {
    selectedSkuDetails: { condition, title, brandName },
  } = useProductPageContext();

  const imageAltAt = (index: number) =>
    buildProductImageAlt({ title, brandName, condition, position: index + 1 });

  const [selectedImage, setSelectedImage] = useState<number>(0);
  const { setSwiper } = useSwiperSlider();
  const swiperRef = useRef<SwiperType | null>(null);

  const handleSwiper = (s: SwiperType) => {
    swiperRef.current = s;
    setSwiper(s);
  };

  useEffect(() => {
    if (swiperRef.current && selectedImage !== swiperRef.current.realIndex) {
      swiperRef.current.slideTo(selectedImage);
    }
  }, [selectedImage]);

  const handleSlideChange = (s: SwiperType) => {
    if (selectedImage !== s.realIndex) {
      setSelectedImage(s.realIndex);
    }
  };

  return (
    <>
      <div className="mt-4 flex items-center gap-x-4">
        <DesktopNavigation
          images={images}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
        />

        <div className="relative h-[250px] w-full rounded-md md:h-[300px] lg:h-[350px]">
          <Swiper
            updateOnWindowResize
            observer
            observeParents
            onSwiper={handleSwiper}
            onSlideChange={handleSlideChange}
            className="h-full w-full"
            slidesPerView={1}
            spaceBetween={0}
          >
            {images.map((img, i) => (
              <SwiperSlide key={img.url} className="!h-full !w-full">
                <div className="relative flex h-full w-full items-center justify-center bg-white">
                  <Image
                    src={img.url}
                    alt={imageAltAt(i)}
                    layout="fill"
                    className="object-contain"
                    loading={i === 0 ? 'eager' : 'lazy'} // Load first image with priority
                  />

                  <div className="absolute right-0 top-0 h-[70px] w-[70px] md:h-20 md:w-20">
                    <Image
                      src={getEdgeUrl(
                        'images/collections/product-page/certified-refurbished.png',
                      )}
                      alt="Certified Refurbished"
                      width={188}
                      height={188}
                      className="h-full w-full"
                      loading={i === 0 ? 'eager' : 'lazy'}
                    />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
      <MobileNavigation
        images={images}
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
      />
    </>
  );
};

const ImageCarousel = () => {
  const { selectedSku, product, isOutOfStock } = useProductPageContext();

  const images = generateCarouselImages(selectedSku, product, isOutOfStock);

  return images?.length ? <Carousel images={images} /> : <Placeholder />;
};

export default ImageCarousel;
